import { Typography } from "antd";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { TableRow as DetailsRow } from "@/views/DataDiscrepancies/Components";
import { linkRenderer, renderCell } from "@/utils/tableConfigs/GlobalFunctions";
import TrendLine from "@/views/Risk/shared/TrendLinePlot/TrendLine";
import {
  extractTrendData,
  calculateBandThresholds,
  RISK_TREND_FIELDS,
} from "@/views/Risk/shared/TrendLinePlot/trendUtils";
import RiskScoreCell from "./RiskScoreCell";
import type { RiskTableRow } from "./risksTableConfig";

const { Title } = Typography;

type RisksDrawerProps = {
  records: RiskTableRow[];
  selectedRecord: RiskTableRow | null;
  setSelectedRecord: (record: RiskTableRow | null) => void;
  isNewRecord?: boolean;
};

export default function RisksDrawer({
  records,
  selectedRecord,
  setSelectedRecord,
  isNewRecord,
}: RisksDrawerProps) {
  const trendData = selectedRecord
    ? extractTrendData(selectedRecord, RISK_TREND_FIELDS)
    : null;
  const bandThresholds = calculateBandThresholds(records, RISK_TREND_FIELDS);

  const headersArray = [
    {
      key: "RISK ID",
      value: (
        <span className="text-white">{selectedRecord?.risk_id || "-"}</span>
      ),
    },
  ];

  return (
    <ResizableDrawer
      headerTitle="Risk Information"
      headersArray={headersArray}
      sizeToggle={{
        collapsedWidth: 654,
        expandedWidth: 1250,
        initialWidth: 654,
      }}
      placement="right"
      open={selectedRecord !== null}
      className="data-management-drawer"
      onClose={() => setSelectedRecord(null)}
      closeIcon={null}
      closable={false}
    >
      {selectedRecord ? (
        <div className="flex flex-col gap-6">
          <section>
            <Title level={5}>Risk Overview</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Risk"
                    value={renderCell(selectedRecord.risk)}
                  />
                  <DetailsRow
                    label="Category"
                    value={renderCell(selectedRecord.category)}
                  />
                  <DetailsRow
                    label="Details"
                    value={renderCell(selectedRecord.details)}
                  />
                  <DetailsRow
                    label="Status"
                    value={renderCell(selectedRecord.status)}
                  />
                  <DetailsRow
                    label="Created By"
                    value={
                      <AvatarWithInitials
                        name={
                          typeof selectedRecord.created_by === "string" &&
                          selectedRecord.created_by.trim().length > 0
                            ? selectedRecord.created_by
                            : null
                        }
                        avatarSize={28}
                        labelFontSize={14}
                      />
                    }
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Risk Scoring</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Probability"
                    value={renderCell(selectedRecord.probability)}
                  />
                  <DetailsRow
                    label="Impact"
                    value={renderCell(selectedRecord.impact)}
                  />
                  <DetailsRow
                    label="Detectability"
                    value={renderCell(selectedRecord.detectability)}
                  />
                  <DetailsRow
                    label="PID Score"
                    value={renderCell(selectedRecord.pid_score)}
                  />
                  <DetailsRow
                    label="Assessed Risk Score"
                    value={
                      <RiskScoreCell
                        value={selectedRecord.assessed_risk_score}
                        band={selectedRecord.band}
                      />
                    }
                  />
                </tbody>
              </table>
            </div>
          </section>
          {!isNewRecord && (
            <>
              {trendData && trendData.trendScores.length > 0 && (
                <section>
                  <Title level={5}>Risk Score Trend</Title>
                  <div className="rounded-sm bg-white p-4">
                    <TrendLine
                      values={trendData.trendScores}
                      statuses={trendData.trendStatuses}
                      thresholds={bandThresholds}
                    />
                  </div>
                </section>
              )}
              <section>
                <Title level={5}>Signals and Monitoring</Title>
                <div className="rounded-sm bg-white">
                  <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                    <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                      <DetailsRow
                        label="Signals"
                        value={renderCell(
                          selectedRecord.signals,
                          linkRenderer((id) => `../signals?openSignal=${id}`),
                        )}
                      />
                    </tbody>
                  </table>
                </div>
              </section>
              <section>
                <Title level={5}>Ownership and Mitigation</Title>
                <div className="rounded-sm bg-white">
                  <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                    <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                      <DetailsRow
                        label="Mitigation Plan"
                        value={renderCell(selectedRecord.mitigation_plan)}
                      />
                      <DetailsRow
                        label="Owners"
                        value={renderCell(selectedRecord.owners)}
                      />
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      ) : null}
    </ResizableDrawer>
  );
}
